// src/services/teamManagementService.js
import { db } from '../firebase';
import { collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, orderBy, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';

/**
 * チームを作成する
 * @param {Object} teamData - チーム作成データ
 * @param {string} creatorUserId - 作成者のユーザーID
 * @returns {Promise<string>} 作成されたチームのID
 */
export const createTeam = async (teamData, creatorUserId) => {
  try {
    const creatorDoc = await getDoc(doc(db, 'users', creatorUserId));
    const creatorData = creatorDoc.data();
    
    const newTeam = {
      name: teamData.name,
      description: teamData.description || '',
      members: [{
        userId: creatorUserId,
        role: 'admin',
        joinedAt: new Date().toISOString(),
        displayName: creatorData?.username || 'Unknown',
        email: creatorData?.email || ''
      }],
      teamAnalytics: { lastAnalyzedAt: null },
      createdAt: new Date().toISOString(),
      createdBy: creatorUserId,
      lastModified: new Date().toISOString(),
    };
    
    const teamDocRef = await addDoc(collection(db, 'teams'), newTeam);
    
    // 作成者のユーザードキュメントにチームメンバーシップを追加
    await updateDoc(doc(db, 'users', creatorUserId), {
      teamMemberships: arrayUnion({
        teamId: teamDocRef.id,
        role: 'admin',
      })
    });
    
    return teamDocRef.id;
  } catch (error) {
    console.error('チーム作成エラー:', error);
    throw new Error('チームの作成に失敗しました');
  }
};

/**
 * チーム一覧を取得する
 * @param {string} userId - ユーザーID
 * @returns {Promise<Array>} チーム一覧
 */
export const getUserTeams = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (!userData?.teamMemberships || userData.teamMemberships.length === 0) {
      return [];
    }
    
    const teamPromises = userData.teamMemberships.map(membership => 
      getDoc(doc(db, 'teams', membership.teamId))
    );
    
    const teamDocs = await Promise.all(teamPromises);
    
    return teamDocs
      .filter(doc => doc.exists())
      .map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('チーム一覧の取得エラー:', error);
    throw new Error('チーム一覧の取得に失敗しました');
  }
};

/**
 * チーム詳細を取得する
 * @param {string} teamId - チームID
 * @returns {Promise<Object>} チーム詳細
 */
export const getTeamDetails = async (teamId) => {
  try {
    const teamDoc = await getDoc(doc(db, 'teams', teamId));
    if (!teamDoc.exists()) {
      throw new Error('チームが見つかりません');
    }
    return { id: teamDoc.id, ...teamDoc.data() };
  } catch (error) {
    console.error('チーム詳細の取得エラー:', error);
    throw new Error('チーム詳細の取得に失敗しました');
  }
};

/**
 * チームにメンバーを追加する
 * @param {string} teamId - チームID
 * @param {string} userId - 追加するユーザーID
 * @param {string} role - 役割
 * @returns {Promise<void>}
 */
export const addTeamMember = async (teamId, userId, role = 'member') => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (!userData) {
      throw new Error('ユーザーが見つかりません');
    }
    
    const newMember = {
      userId: userId,
      role: role,
      joinedAt: new Date().toISOString(),
      displayName: userData.username || 'Unknown',
      email: userData.email || ''
    };
    
    // チームにメンバーを追加
    await updateDoc(doc(db, 'teams', teamId), {
      members: arrayUnion(newMember),
      lastModified: new Date().toISOString()
    });
    
    // ユーザーのチームメンバーシップを更新
    await updateDoc(doc(db, 'users', userId), {
      teamMemberships: arrayUnion({
        teamId: teamId,
        role: role,
      })
    });
    
  } catch (error) {
    console.error('チームメンバー追加エラー:', error);
    throw new Error('チームメンバーの追加に失敗しました');
  }
};

/**
 * チームからメンバーを削除する
 * @param {string} teamId - チームID
 * @param {string} userId - 削除するユーザーID
 * @returns {Promise<void>}
 */
export const removeTeamMember = async (teamId, userId) => {
  try {
    const teamDoc = await getDoc(doc(db, 'teams', teamId));
    const teamData = teamDoc.data();
    
    if (!teamData) {
      throw new Error('チームが見つかりません');
    }
    
    // 削除するメンバーを特定
    const memberToRemove = teamData.members.find(member => member.userId === userId);
    if (!memberToRemove) {
      throw new Error('メンバーが見つかりません');
    }
    
    // チームからメンバーを削除
    await updateDoc(doc(db, 'teams', teamId), {
      members: arrayRemove(memberToRemove),
      lastModified: new Date().toISOString()
    });
    
    // ユーザーのチームメンバーシップを更新
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (userData?.teamMemberships) {
      const updatedMemberships = userData.teamMemberships.filter(
        membership => membership.teamId !== teamId
      );
      
      await updateDoc(doc(db, 'users', userId), {
        teamMemberships: updatedMemberships
      });
    }
    
  } catch (error) {
    console.error('チームメンバー削除エラー:', error);
    throw new Error('チームメンバーの削除に失敗しました');
  }
};

/**
 * 1on1記録を保存する
 * @param {string} recordId - 記録ID（新規の場合はnull）
 * @param {Object} recordData - 記録データ
 * @returns {Promise<string>} 記録ID
 */
export const saveOneOnOneRecord = async (recordId, recordData) => {
  try {
    if (recordId) {
      const docRef = doc(db, 'oneOnOneRecords', recordId);
      await updateDoc(docRef, { 
        ...recordData, 
        updatedAt: serverTimestamp() 
      });
      return recordId;
    } else {
      const collectionRef = collection(db, 'oneOnOneRecords');
      const newRecord = { 
        ...recordData, 
        createdAt: serverTimestamp(), 
        updatedAt: serverTimestamp() 
      };
      const docRef = await addDoc(collectionRef, newRecord);
      return docRef.id;
    }
  } catch (error) {
    console.error('1on1記録の保存エラー:', error);
    throw new Error('1on1記録の保存に失敗しました。');
  }
};

/**
 * 1on1履歴を取得する
 * @param {string} memberId - メンバーID
 * @returns {Promise<Array>} 1on1履歴
 */
export const getOneOnOneHistory = async (memberId) => {
  try {
    const q = query(
      collection(db, 'oneOnOneRecords'),
      where('memberId', '==', memberId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const history = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      meetingDate: doc.data().createdAt?.toDate(),
    }));
    return history;
  } catch (error) {
    console.error('1on1履歴の取得エラー:', error);
    throw new Error('1on1履歴の取得に失敗しました。');
  }
};

/**
 * チームを削除する
 * @param {string} teamId - チームID
 * @returns {Promise<void>}
 */
export const deleteTeam = async (teamId) => {
  try {
    // チーム詳細を取得
    const teamDoc = await getDoc(doc(db, 'teams', teamId));
    const teamData = teamDoc.data();
    
    if (!teamData) {
      throw new Error('チームが見つかりません');
    }
    
    // 全メンバーのチームメンバーシップを削除
    const memberUpdates = teamData.members.map(async (member) => {
      const userDoc = await getDoc(doc(db, 'users', member.userId));
      const userData = userDoc.data();
      
      if (userData?.teamMemberships) {
        const updatedMemberships = userData.teamMemberships.filter(
          membership => membership.teamId !== teamId
        );
        
        await updateDoc(doc(db, 'users', member.userId), {
          teamMemberships: updatedMemberships
        });
      }
    });
    
    await Promise.all(memberUpdates);
    
    // チームを削除
    await deleteDoc(doc(db, 'teams', teamId));
    
  } catch (error) {
    console.error('チーム削除エラー:', error);
    throw new Error('チームの削除に失敗しました');
  }
};

/**
 * チーム情報を更新する
 * @param {string} teamId - チームID
 * @param {Object} updateData - 更新データ
 * @returns {Promise<void>}
 */
export const updateTeam = async (teamId, updateData) => {
  try {
    await updateDoc(doc(db, 'teams', teamId), {
      ...updateData,
      lastModified: new Date().toISOString()
    });
  } catch (error) {
    console.error('チーム更新エラー:', error);
    throw new Error('チームの更新に失敗しました');
  }
};